import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { cn } from '@/lib/utils'
import { ICONS } from '@/lib/icons'
import { toFaDigits } from '@/lib/format'
import { useDrafts, useFavorites } from '@/components/generals/providers'
import { buttonVariants } from '@/components/ds/button'
import { ARTICLES } from '@/constants/articles'
import { CURRENT_USER_SLUG } from '@/constants/people'

import {
    NAV_GROUPS,
    PRIMARY_ACTION,
    activeNavHref
} from '@/constants/navigation'

const RETURNED_COUNT = ARTICLES.filter(article => article.author === CURRENT_USER_SLUG && article.status === 'returned').length

export const useCurrentPath = (): string => {
    const router = useRouter()

    return router.asPath.split(/[?#]/)[0] || '/'
}

interface NavListProps {
    collapsed?: boolean
    onNavigate?: () => void
}

export const NavList: FC<NavListProps> = ({ collapsed = false, onNavigate }): ReactNode => {
    const path = useCurrentPath()
    const active = activeNavHref(path)
    const { favorites, ready: favoritesReady } = useFavorites()
    const { drafts, ready: draftsReady } = useDrafts()
    const PrimaryIcon = ICONS[PRIMARY_ACTION.icon]

    const attention = RETURNED_COUNT + (draftsReady ? drafts.filter(draft => draft.status === 'draft').length : 0)

    const badgeFor = (badge?: 'favorites' | 'mine'): { value: number, tone: string, label: string } | null => {
        if (badge === 'favorites' && favoritesReady && favorites.length) {
            return { value: favorites.length, tone: 'bg-secondary text-muted-foreground', label: 'دانش ذخیره‌شده' }
        }

        if (badge === 'mine' && attention) {
            return { value: attention, tone: 'bg-warning-soft text-warning-strong', label: 'مورد نیازمند اقدام' }
        }

        return null
    }

    return (
        <div className='flex flex-col gap-6'>
            <Link
                href={PRIMARY_ACTION.href}
                onClick={onNavigate}
                aria-current={active === PRIMARY_ACTION.href ? 'page' : undefined}
                title={collapsed ? PRIMARY_ACTION.label : undefined}
                className={buttonVariants({
                    variant: 'primary',
                    size: collapsed ? 'icon' : 'md',
                    className: cn('w-full', collapsed && 'mx-auto size-11 w-11', active === PRIMARY_ACTION.href && 'ring-2 ring-primary/30 ring-offset-2')
                })}
            >
                <PrimaryIcon aria-hidden='true' />
                <span className={cn(collapsed && 'sr-only')}>{PRIMARY_ACTION.label}</span>
            </Link>

            {NAV_GROUPS.map(group => (
                <div key={group.title} className='flex flex-col gap-1'>
                    <p className={cn('px-3 pb-1 text-caption font-semibold text-subtle', collapsed && 'sr-only')}>
                        {group.title}
                    </p>

                    <ul className='flex flex-col gap-0.5'>
                        {group.items.map(item => {
                            const Icon = ICONS[item.icon]
                            const isActive = active === item.href
                            const badge = badgeFor(item.badge)

                            return (
                                <li key={item.href}>
                                    <Link
                                        href={item.href}
                                        onClick={onNavigate}
                                        aria-current={isActive ? 'page' : undefined}
                                        title={collapsed ? item.label : undefined}
                                        data-active={isActive}
                                        className={cn(
                                            'group relative flex h-10 items-center gap-3 rounded-lg text-sm font-medium text-foreground/80 transition-colors duration-150 hover:bg-secondary hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/45 data-[active=true]:bg-primary-soft data-[active=true]:font-semibold data-[active=true]:text-primary-deep',
                                            collapsed ? 'justify-center px-0' : 'px-3'
                                        )}
                                    >
                                        <span
                                            className='absolute inset-y-2 start-0 w-1 rounded-full bg-primary opacity-0 transition-opacity group-data-[active=true]:opacity-100'
                                            aria-hidden='true'
                                        />

                                        <Icon className='size-5 shrink-0 text-subtle transition-colors group-hover:text-navy group-data-[active=true]:text-primary' aria-hidden='true' />

                                        <span className={cn('flex-1 truncate', collapsed && 'sr-only')}>{item.label}</span>

                                        {badge && (
                                            <span
                                                className={cn(
                                                    'flex min-w-5 items-center justify-center rounded-full px-1.5 text-caption font-semibold tabular-nums',
                                                    badge.tone,
                                                    collapsed && 'absolute end-1.5 top-1 min-w-4 px-1 text-[10px]'
                                                )}
                                                title={`${toFaDigits(badge.value)} ${badge.label}`}
                                            >
                                                {toFaDigits(badge.value)}
                                            </span>
                                        )}
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>
                </div>
            ))}
        </div>
    )
}
